// Find measures on a rendered score page (pure; RGBA pixels in, boxes out).
//
//   1. staff lines   = rows dominated by THIN ink (survives beams/rest bars crossing the line)
//   2. staves        = 4–6 lines with near-equal spacing (5 = notation, 6 = guitar TAB)
//   3. barlines      = columns that are dark across (almost) the whole staff height, alone (no ink
//                      beside them); close lines merge into one group (double / repeat barlines).
//                      Repeat dots beside a group (‖: or :‖) are recorded as repeat start / end
//   4. systems       = adjacent staves sharing barline positions (notation + TAB pairs)
//   5. measures      = spans between consecutive barlines of a system, in reading order
//   6. multi-rests   = a thick horizontal bar across the middle of a measure ("5" above it = 5 bars);
//                      flagged here, the number is read from the PDF text layer by the caller
//
// Works on clean digital PDFs (Guitar Pro / MuseScore / Sibelius exports). Skewed scans won't produce
// horizontal staff lines, so they return no measures and the caller falls back to "no sync".

export interface Pixels { width: number; height: number; data: Uint8ClampedArray }
export interface Box {
  x0: number; x1: number; y0: number; y1: number;   // normalized 0..1 of the page
  staffTop: number;                                  // normalized y of the system's top staff line
  multiRest?: boolean;                               // H-bar multi-measure rest (bar count comes from the text layer)
  span?: number;                                     // bars this box stands for (multi-measure rest); default 1
  repeatStart?: boolean;                             // ‖: at the box's left edge
  repeatEnd?: boolean;                               // :‖ at the box's right edge
  ending?: number;                                   // first bar of volta "1." / "2." (from the text layer)
  chords?: string[];                                 // chord symbols printed above the bar (text layer), left→right
}
export interface PageLayout { measures: Box[]; systems: number; staves: number }

interface Group { x: number; xa: number; xb: number; dotsL: boolean; dotsR: boolean }   // one barline (or double/repeat) position
interface Staff { lines: number[]; top: number; bottom: number; space: number; left: number; right: number; bars: number[]; groups: Group[]; pokeUp: number[]; pokeDown: number[] }

const DARK = 200;   // luminance below this counts as ink (thin lines render as anti-aliased gray)

export function detectMeasures(px: Pixels): PageLayout {
  const { width: W, height: H, data } = px;
  const dark = new Uint8Array(W * H);
  for (let i = 0, p = 0; i < W * H; i++, p += 4) {
    const a = data[p + 3] / 255;                                   // transparent = paper
    const lum = (0.299 * data[p] + 0.587 * data[p + 1] + 0.114 * data[p + 2]) * a + 255 * (1 - a);
    dark[i] = lum < DARK ? 1 : 0;
  }

  // 1. staff-line rows. Count only THIN ink (vertical run ≤ THIN px): beams, multi-measure-rest bars
  //    and lyrics fused with a staff line would otherwise turn it into one thick block and hide it.
  //    A staff line stays thin across most of its width even where a beam crosses it.
  const THIN = Math.max(3, Math.round(H / 650));
  const thinRow = new Uint32Array(H);
  for (let x = 0; x < W; x++) {
    for (let y = 0; y < H;) {
      if (!dark[y * W + x]) { y++; continue; }
      const y0 = y;
      while (y < H && dark[y * W + x]) y++;
      if (y - y0 <= THIN) for (let k = y0; k < y; k++) thinRow[k]++;
    }
  }
  const lineRows: number[] = [];
  for (let y = 0; y < H; y++) if (thinRow[y] / W > 0.3) lineRows.push(y);
  const lines: number[] = [];
  for (let k = 0; k < lineRows.length;) {
    let j = k;
    while (j + 1 < lineRows.length && lineRows[j + 1] - lineRows[j] <= 1) j++;
    if (lineRows[j] - lineRows[k] <= THIN) lines.push((lineRows[k] + lineRows[j]) / 2);
    k = j + 1;
  }

  // 2. staves: runs of 4–6 equally spaced lines
  const staves: Staff[] = [];
  for (let k = 0; k < lines.length;) {
    let j = k + 1;
    const g = j < lines.length ? lines[j] - lines[k] : 0;
    if (g >= 4 && g <= H / 25) {
      while (j + 1 < lines.length && Math.abs(lines[j + 1] - lines[j] - g) <= Math.max(1.5, g * 0.2)) j++;
    }
    const count = j - k + 1;
    if (g >= 4 && count >= 4 && count <= 6) {
      const ls = lines.slice(k, j + 1);
      const yMid = Math.round(ls[0]);
      let left = 0, right = W - 1;
      while (left < W && !dark[yMid * W + left]) left++;
      while (right > left && !dark[yMid * W + right]) right--;
      staves.push({ lines: ls, top: ls[0], bottom: ls[ls.length - 1], space: g, left, right, bars: [], groups: [], pokeUp: [], pokeDown: [] });
      k = j + 1;
    } else k++;
  }

  // 3. barlines per staff: ink across ≥ 95% of the staff height, a few px wide at most
  for (const s of staves) {
    const y0 = Math.round(s.top), y1 = Math.round(s.bottom), h = y1 - y0 + 1;
    const runs: [number, number][] = [];
    let start = -1;
    for (let x = s.left; x <= s.right + 1; x++) {
      let n = 0;
      if (x <= s.right) for (let y = y0; y <= y1; y++) n += dark[y * W + x];
      const hit = x <= s.right && n / h >= 0.95;
      if (hit && start < 0) start = x;
      if (!hit && start >= 0) { runs.push([start, x - 1]); start = -1; }
    }
    const maxW = Math.max(3, s.space * 0.7);
    // a barline stands alone: the few px beside it hold only staff lines. Time-signature digits (two
    // stacked 4s line up into one full-height stroke), clefs and note clusters have ink right next to
    // them. Other full-height columns (the thin+thick of a double bar) don't count as ink.
    const isCand = (x: number) => runs.some(([a, b]) => x >= a && x <= b);
    const onLine = (y: number) => s.lines.some((l) => Math.abs(y - l) <= 1.5);
    const sideInk = (xa: number, xb: number) => {
      let n = 0, tot = 0;
      for (let x = Math.max(0, xa); x <= Math.min(W - 1, xb); x++) {
        if (isCand(x)) continue;
        for (let y = y0; y <= y1; y++) { if (onLine(y)) continue; tot++; n += dark[y * W + x]; }
      }
      return tot ? n / tot : 0;
    };
    const sp = s.space;
    const groups: Group[] = [];
    for (const [a, b] of runs) {
      if (b - a + 1 > maxW) continue;                              // a filled block, not a line
      // ink beside the line rejects it — unless that ink is a pair of repeat dots
      if (sideInk(a - 7, a - 2) > 0.25 && !hasDots(s, a - sp * 1.1, a - sp * 0.15)) continue;
      if (sideInk(b + 2, b + 7) > 0.25 && !hasDots(s, b + sp * 0.15, b + sp * 1.1)) continue;
      const x = (a + b) / 2;
      // double / repeat barlines: one group spanning both lines
      const last = groups[groups.length - 1];
      if (last && x - last.x < sp * 1.5) { last.x = x; last.xb = b; }
      else groups.push({ x, xa: a, xb: b, dotsL: false, dotsR: false });
    }
    for (const gr of groups) {
      gr.dotsL = hasDots(s, gr.xa - sp * 1.1, gr.xa - sp * 0.15);
      gr.dotsR = hasDots(s, gr.xb + sp * 0.15, gr.xb + sp * 1.1);
    }
    s.groups = groups;
    const bars = groups.map((gr) => gr.x);
    s.bars = bars;
    // a note stem can also cross the whole staff, but it keeps going past the top or bottom line; a
    // barline never sticks out above a system's top staff or below its bottom staff (inside a grand
    // staff it may continue through the gap). Remember which lines poke out, per direction.
    const ext = (x: number, from: number, to: number) => {
      let n = 0, tot = 0;
      for (let y = Math.max(0, Math.round(from)); y <= Math.min(H - 1, Math.round(to)); y++, tot++)
        n += dark[y * W + x] | dark[y * W + x - 1] | dark[y * W + x + 1];
      return tot > 0 && n / tot >= 0.6;
    };
    s.pokeUp = bars.filter((x) => ext(Math.round(x), s.top - s.space, s.top - s.space * 0.25));
    s.pokeDown = bars.filter((x) => ext(Math.round(x), s.bottom + s.space * 0.25, s.bottom + s.space));
  }

  // 4. systems. Every system on a page tends to share barline positions, so that alone can't split
  //    them. Two concrete signals join a staff to the one above it:
  //    - barlines physically continue through the gap (brace/ensemble systems), or
  //    - a 6-line TAB staff right under a 5-line notation staff with matching barlines (guitar scores)
  const connected = (a: Staff, b: Staff) => {
    const g0 = Math.round(a.bottom) + 1, g1 = Math.round(b.top) - 1;
    if (g1 < g0) return true;
    return a.bars.some((x) => b.bars.some((y) => Math.abs(x - y) <= 2) && (() => {
      const cx = Math.round(x);
      let n = 0;
      for (let y = g0; y <= g1; y++) n += dark[y * W + cx] | dark[y * W + cx - 1] | dark[y * W + cx + 1];
      return n / (g1 - g0 + 1) >= 0.9;
    })());
  };
  const systems: Staff[][] = [];
  for (const s of staves) {
    const cur = systems[systems.length - 1];
    const prev = cur?.[cur.length - 1];
    const near = prev && s.top - prev.bottom < (prev.bottom - prev.top) * 3;
    const tabUnderNotation = prev && cur.length === 1 && prev.lines.length === 5 && s.lines.length === 6 && sharedBars(prev, s) >= 0.6;
    if (prev && near && (connected(prev, s) || tabUnderNotation)) cur.push(s);
    else systems.push([s]);
  }

  // 5. measures in reading order
  const measures: Box[] = [];
  for (const sys of systems) {
    const ref = sys[0];
    // a barline appears in a strict majority of the system's staves (stems rarely line up across staves)
    // and doesn't poke above the top staff or below the bottom one (stems do)
    const lowest = sys[sys.length - 1];
    const near = (xs: number[], x: number) => xs.some((b) => Math.abs(b - x) <= ref.space * 0.5);
    const bars = ref.bars.filter((x) =>
      sys.filter((t) => t.bars.some((b) => Math.abs(b - x) <= ref.space)).length * 2 > sys.length
      && !near(ref.pokeUp, x) && !near(lowest.pokeDown, x));
    const left = Math.min(...sys.map((t) => t.left)), right = Math.max(...sys.map((t) => t.right));
    const groupAt = (x: number) => ref.groups.find((gr) => gr.x === x);
    const edges = [...bars];
    if (!edges.length || edges[0] - left > ref.space * 1.5) edges.unshift(left);    // system start without a line
    // a ‖: right after the clef/key/time opens the first measure: the narrow strip before it isn't one
    if (edges.length > 2 && groupAt(edges[1])?.dotsR && edges[1] - edges[0] < ref.space * 12) edges.shift();
    if (right - edges[edges.length - 1] > ref.space * 4) edges.push(right);          // open end (no final line)
    const top = sys[0].top, bottom = sys[sys.length - 1].bottom;
    const pad = ref.space;
    for (let i = 0; i + 1 < edges.length; i++) {
      if (edges[i + 1] - edges[i] < ref.space * 2) continue;       // sliver between close lines
      measures.push({
        x0: edges[i] / W, x1: edges[i + 1] / W,
        y0: Math.max(0, top - pad * 2.5) / H, y1: Math.min(H, bottom + pad * 1.5) / H,
        staffTop: top / H,
        ...(hasHBar(ref, edges[i], edges[i + 1]) ? { multiRest: true } : {}),
        ...(groupAt(edges[i])?.dotsR ? { repeatStart: true } : {}),
        ...(groupAt(edges[i + 1])?.dotsL ? { repeatEnd: true } : {}),
      });
    }
  }
  return { measures, systems: systems.length, staves: staves.length };

  // repeat dots: one small blob in each of the two inner spaces (5 lines: spaces 2+3, TAB: 2+4), the
  // outer spaces empty — notes and rests fill other spaces or several at once
  function hasDots(st: Staff, xa: number, xb: number): boolean {
    const n = st.lines.length;
    const pair = n === 6 ? [1, 3] : n === 5 ? [1, 2] : null;
    if (!pair) return false;
    const a = Math.max(0, Math.round(xa)), b = Math.min(W - 1, Math.round(xb));
    const inSpace = (k: number) => {
      const yc = (st.lines[k] + st.lines[k + 1]) / 2, ry = st.space * 0.3;
      let ink = 0;
      for (let y = Math.round(yc - ry); y <= Math.round(yc + ry); y++) for (let x = a; x <= b; x++) ink += dark[y * W + x];
      return ink >= st.space * st.space * 0.08;
    };
    return pair.every(inSpace) && !inSpace(0) && !inSpace(n - 2);
  }

  // multi-measure rest: across most of the measure, the ink through the middle staff line is a thick
  // vertical run (the H-bar is ~1 space tall). Note heads are thick only a few columns at a time.
  function hasHBar(st: Staff, xa: number, xb: number): boolean {
    const ym = Math.round(st.lines[Math.floor(st.lines.length / 2)]);
    const lo = Math.round(st.top + st.space * 0.5), hi = Math.round(st.bottom - st.space * 0.5);
    const a = Math.round(xa + (xb - xa) * 0.15), b = Math.round(xb - (xb - xa) * 0.15);
    let thick = 0;
    for (let x = a; x <= b; x++) {
      if (!dark[ym * W + x]) continue;
      let u = ym, d = ym;
      while (u > lo && dark[(u - 1) * W + x]) u--;
      while (d < hi && dark[(d + 1) * W + x]) d++;
      if (d - u + 1 >= st.space * 0.6) thick++;
    }
    return b > a && thick / (b - a + 1) >= 0.6;
  }
}

function sharedBars(a: Staff, b: Staff): number {
  if (!a.bars.length || !b.bars.length) return 0;
  const tol = Math.max(a.space, b.space);
  const hit = a.bars.filter((x) => b.bars.some((y) => Math.abs(x - y) <= tol)).length;
  return hit / Math.max(a.bars.length, b.bars.length);
}
